import * as React from 'react';
import {
  Stack,
  Divider,
  Accordion,
  Template,
  Paragraph,
  Box,
  Icon,
  Cluster,
  Click,
  Popover,
  FormItem,
  TextInput,
  Button,
  Sidebar,
  Heading,
  useValidateForm,
  Spinner,
  Toast,
} from '@pluto-tv/assemble';
import CrudError from 'components/crudError';
import DeleteConfirmation from 'components/deleteConfirmation';
import {usePreferences} from 'helpers/useGenericPreferences';
import {popoverActionsProps} from 'helpers/popoverActionProps';
import {cloneDeep, filter, find, reject} from 'lodash-es';
import {IUserSearch} from 'models/users';

export interface IFavoriteSearchProps<TSearch, TMeta> {
  searchModel?: IUserSearch<TSearch, TMeta>;
  getValidation?: () => Promise<Partial<TSearch>>;
  onSearchSelected: (search: IUserSearch<TSearch, TMeta>) => void;
  onSearchCreated: (search: IUserSearch<TSearch, TMeta>) => void;
  searchSelected?: IUserSearch<TSearch, TMeta>;
  onClearSelection: () => void;
  entity: string;
}

const GenericFavoriteSearch = <TSearch, TMeta>({
  searchModel,
  getValidation,
  onSearchSelected,
  onSearchCreated,
  searchSelected,
  onClearSelection,
  entity,
}: IFavoriteSearchProps<TSearch, TMeta>): JSX.Element => {
  const {isFetching, isError, searches, updateSavedSearches} = usePreferences<TSearch>(entity);

  const [drawerOpen, setDrawerOpen] = React.useState<boolean>(false);

  const handleUpdateSearch = async (searches: any[]) => {
    updateSavedSearches(entity, searches);
    setDrawerOpen(true);
  };

  const handleSearchSelected = (search: any) => {
    if (!search) {
      onClearSelection();
      return;
    }
    onSearchSelected(search);
  };

  return (
    <FavoriteSearch
      searchModel={searchModel}
      getValidation={getValidation}
      isFetching={isFetching}
      isError={isError}
      searches={searches}
      onUpdateSearches={handleUpdateSearch}
      onSearchSelected={handleSearchSelected}
      onSearchCreated={onSearchCreated}
      activeSearch={searchSelected}
      drawerOpen={drawerOpen}
    />
  );
};

GenericFavoriteSearch.displayName = 'GenericFavoriteSearch';
export {GenericFavoriteSearch};

interface IGenericFavoriteSearchParams<TSearch, TMeta> {
  searchModel?: IUserSearch<TSearch, TMeta>;
  getValidation?: () => Promise<Partial<IUserSearch<TSearch, TMeta>>>;
  isFetching: boolean;
  isError: boolean;
  searches: any[];
  onUpdateSearches: (searches: any[]) => Promise<void>;
  onSearchSelected: (search: any) => void;
  onSearchCreated: (search: any) => void;
  activeSearch?: IUserSearch<TSearch, TMeta>;
  drawerOpen?: boolean;
}
const FavoriteSearch = React.memo(
  <TSearch, TMeta>({
    searchModel,
    getValidation,
    isFetching,
    isError,
    searches,
    onUpdateSearches,
    onSearchSelected,
    onSearchCreated,
    activeSearch,
    drawerOpen = false,
  }: IGenericFavoriteSearchParams<TSearch, TMeta>) => {
    const [isFavSearchCreateOpen, setIsFavSearchCreateOpen] = React.useState(false);
    const [isFavSearchSaveAsOpen, setIsFavSearchSaveAsOpen] = React.useState(false);
    const [isSaveConfirmationOpen, setIsSaveConfirmationOpen] = React.useState(false);
    const [searchDeleteVisible, setSearchDeleteVisible] = React.useState<boolean[]>([]);
    const [searchRenameVisible, setSearchRenameVisible] = React.useState<boolean[]>([]);
    const [editSearch, setEditSearch] = React.useState<number>();

    const {
      model: searchingModel,
      form: searchingForm,
      onChange: searchingOnChange,
      onBlur: searchingOnBlur,
      setCustomErrorMsg: searchingSetCustomErrorMsg,
      setModel: searchingSeTMeta,
      state: searchingState,
    } = useValidateForm<{search: string}>([{name: 'search'}]);

    const getSearchModel = async (): Promise<IUserSearch<TSearch, TMeta> | undefined> => {
      if (getValidation) {
        const {model} = await getValidation();

        return {
          ...searchModel!,
          model: model as any,
        };
      }

      return searchModel;
    };

    const createSavedSearch = async () => {
      const search = await getSearchModel();

      const newSearch = {
        ...search,
        name: searchingModel.search?.trim(),
      } as IUserSearch<TSearch, TMeta>;

      if (find(searches, search => search.name === searchingModel.search?.trim())) {
        searchingSetCustomErrorMsg(
          'search',
          'Saved search with that name already exists! Please choose a different name.',
        );
        return;
      }

      try {
        await onUpdateSearches([...searches, newSearch]);

        onSearchCreated(newSearch);

        Toast.success('Created new saved search');

        setIsFavSearchSaveAsOpen(false);
        setIsFavSearchCreateOpen(false);
      } catch (e) {
        Toast.error('Error creating new saved search. Please try again');
      }
    };

    const renameSavedSearch = async () => {
      if (editSearch === undefined) {
        return;
      }

      if (
        find(
          reject(searches, search => search.name === searches[editSearch].name),
          search => search.name === searchingModel.search?.trim(),
        )
      ) {
        searchingSetCustomErrorMsg(
          'search',
          'Saved search with that name already exists! Please choose a different name.',
        );
        return;
      }

      try {
        const updatedSearches = cloneDeep(searches);
        updatedSearches[editSearch].name = searchingModel.search?.trim();

        if (activeSearch?.name === searches[editSearch].name) {
          onSearchSelected({
            ...(activeSearch as IUserSearch<TSearch, TMeta>),
            name: searchingModel.search!,
          });
        }

        await onUpdateSearches(updatedSearches);

        Toast.success('Renamed Search Successfully');

        setSearchRenameVisible([]);
        setEditSearch(undefined);
      } catch (e) {
        Toast.error('Error renaming saved search. Please try again');
      }
    };

    if (isError) {
      return <CrudError error='Error loading favorite searches' />;
    }

    if (isFetching) {
      return (
        <Box fullHeight={true}>
          <Spinner center={true} minHeight='9.375rem' size='xlarge' />
        </Box>
      );
    }

    return (
      <Stack space='medium'>
        <Divider color='graphite' />
        <Accordion isOpen={drawerOpen}>
          <Template label='trigger'>
            <Paragraph>Favorite Searches</Paragraph>
          </Template>
          <Template label='body'>
            <Box marginTop='small' marginBottom='xxxxxsmall' marginLeft='medium'>
              <Stack space='medium'>
                <Box maxHeight='9.375rem'>
                  <Stack space='medium'>
                    {searches.length === 0 ? (
                      <Icon icon='warning' color='warning' space='xxsmall'>
                        No Saved Searches
                      </Icon>
                    ) : (
                      searches.map((savedSearch, i) => (
                        <Cluster space='small' align='center' wrap={false} key={savedSearch.name}>
                          <Click
                            size='small'
                            truncate={true}
                            truncateBackgroundHover='pewter'
                            color='primary'
                            hoverColor='primaryLight'
                            underline={activeSearch?.name === savedSearch.name}
                            onClick={() => onSearchSelected(savedSearch)}
                          >
                            {savedSearch.name}
                          </Click>
                          <Popover
                            appendToBody={true}
                            manualTrigger={true}
                            visible={searchRenameVisible[i]}
                            onClickOutside={() => {
                              setEditSearch(undefined);
                              setSearchRenameVisible([]);
                            }}
                          >
                            <Template label='trigger'>
                              <Icon
                                color='neutral'
                                icon='edit'
                                size='small'
                                onClick={() => {
                                  setEditSearch(i);
                                  searchingSetCustomErrorMsg('search', '');
                                  searchingSeTMeta({
                                    search: savedSearch.name,
                                  });
                                  setSearchRenameVisible([...new Array(i), true]);
                                }}
                              />
                            </Template>
                            <Template label='popover'>
                              <Box
                                padding={popoverActionsProps.padding}
                                background={popoverActionsProps.background}
                                width={popoverActionsProps.width}
                              >
                                <Stack space='small'>
                                  <FormItem
                                    {...searchingForm.search}
                                    label='Search Name'
                                    state={
                                      searchingForm.search?.helpText && searchingForm.search?.helpText.length > 0
                                        ? 'error'
                                        : undefined
                                    }
                                    onBlur={() => searchingOnBlur('search')}
                                  >
                                    <TextInput
                                      value={searchingModel.search}
                                      onChange={val => searchingOnChange('search', val)}
                                    />
                                  </FormItem>
                                  <Cluster justify='space-between'>
                                    <div></div>
                                    <Cluster>
                                      <Button
                                        ghost={true}
                                        onClick={() => {
                                          setEditSearch(undefined);
                                          setSearchRenameVisible([]);
                                        }}
                                      >
                                        Cancel
                                      </Button>
                                      <Button
                                        type='primary'
                                        onClick={renameSavedSearch}
                                        state={
                                          isFetching
                                            ? 'thinking'
                                            : !searchingState.isDirty ||
                                              !searchingState.isValid ||
                                              !searchingModel.search?.trim()
                                            ? 'disabled'
                                            : ''
                                        }
                                      >
                                        Save
                                      </Button>
                                    </Cluster>
                                  </Cluster>
                                </Stack>
                              </Box>
                            </Template>
                          </Popover>
                          <Popover
                            appendToBody={true}
                            manualTrigger={true}
                            visible={searchDeleteVisible[i]}
                            onClickOutside={() => setSearchDeleteVisible([])}
                          >
                            <Template label='trigger'>
                              <Icon
                                color='delete'
                                hoverColor='delete'
                                icon='delete'
                                size='small'
                                onClick={() => setSearchDeleteVisible([...new Array(i), true])}
                              />
                            </Template>
                            <Template label='popover'>
                              <DeleteConfirmation
                                message='Are you sure you want to delete this search?'
                                cancelButtonFunction={() => setSearchDeleteVisible([])}
                                proceedButtonFunction={async () => {
                                  await onUpdateSearches(filter(searches, search => search.name !== savedSearch.name));

                                  if (savedSearch.name === activeSearch?.name) {
                                    onSearchSelected(undefined);
                                  }

                                  setSearchDeleteVisible([]);
                                }}
                              />
                            </Template>
                          </Popover>
                        </Cluster>
                      ))
                    )}
                  </Stack>
                </Box>
                <Cluster justify='space-between'>
                  <div></div>
                  {activeSearch ? (
                    <Cluster space='small'>
                      <Popover
                        appendToBody={true}
                        manualTrigger={true}
                        visible={isFavSearchSaveAsOpen}
                        onClickOutside={() => setIsFavSearchSaveAsOpen(false)}
                      >
                        <Template label='trigger'>
                          <Button
                            size='small'
                            type='primary'
                            onClick={() => {
                              searchingSetCustomErrorMsg('search', '');
                              searchingSeTMeta({
                                search: '',
                              });
                              setIsFavSearchSaveAsOpen(true);
                            }}
                          >
                            Save As
                          </Button>
                        </Template>
                        <Template label='popover'>
                          <Box
                            padding={popoverActionsProps.padding}
                            background={popoverActionsProps.background}
                            width={popoverActionsProps.width}
                          >
                            <Stack space='small'>
                              <FormItem
                                {...searchingForm.search}
                                label='Search Name'
                                state={
                                  searchingForm.search?.helpText && searchingForm.search?.helpText.length > 0
                                    ? 'error'
                                    : undefined
                                }
                                onBlur={() => searchingOnBlur('search')}
                              >
                                <TextInput
                                  value={searchingModel.search}
                                  onChange={val => searchingOnChange('search', val)}
                                />
                              </FormItem>
                              <Cluster justify='space-between'>
                                <div></div>
                                <Cluster>
                                  <Button ghost={true} onClick={() => setIsFavSearchSaveAsOpen(false)}>
                                    Cancel
                                  </Button>
                                  <Button
                                    type='primary'
                                    onClick={createSavedSearch}
                                    state={
                                      isFetching
                                        ? 'thinking'
                                        : !searchingState.isDirty ||
                                          !searchingState.isValid ||
                                          !searchingModel?.search?.trim()
                                        ? 'disabled'
                                        : ''
                                    }
                                  >
                                    Save As
                                  </Button>
                                </Cluster>
                              </Cluster>
                            </Stack>
                          </Box>
                        </Template>
                      </Popover>
                      <Popover
                        appendToBody={true}
                        manualTrigger={true}
                        maxWidth='22rem'
                        visible={isSaveConfirmationOpen}
                        onClickOutside={() => setIsSaveConfirmationOpen(false)}
                      >
                        <Template label='trigger'>
                          <Button size='small' type='primary' onClick={() => setIsSaveConfirmationOpen(true)}>
                            Save
                          </Button>
                        </Template>
                        <Template label='popover'>
                          <Box padding='xxsmall'>
                            <Sidebar gap='large'>
                              <Icon icon='warning' size='xxxlarge' />
                              <Box paddingTop='xsmall'>
                                <Stack space='large'>
                                  <Heading level='h3'>Are you sure?</Heading>
                                  <Paragraph>
                                    You are about to override your saved search titled &apos;{activeSearch.name}&apos;.
                                  </Paragraph>
                                  <Cluster justify='space-between'>
                                    <div></div>
                                    <Cluster space='xxxxsmall'>
                                      <Button ghost={true} onClick={() => setIsSaveConfirmationOpen(false)}>
                                        Cancel
                                      </Button>
                                      <Button
                                        type='primary'
                                        onClick={async () => {
                                          const allSearches = cloneDeep(searches);
                                          const gotSearch = await getSearchModel();

                                          allSearches.forEach(search => {
                                            if (search.name === activeSearch.name) {
                                              search.meta = searchModel?.meta || {};
                                              search.model = gotSearch?.model || {};
                                            }
                                          });

                                          await onUpdateSearches(allSearches);
                                          setIsSaveConfirmationOpen(false);
                                        }}
                                      >
                                        Yes, Proceed
                                      </Button>
                                    </Cluster>
                                  </Cluster>
                                </Stack>
                              </Box>
                            </Sidebar>
                          </Box>
                        </Template>
                      </Popover>
                    </Cluster>
                  ) : (
                    <Popover
                      appendToBody={true}
                      manualTrigger={true}
                      visible={isFavSearchCreateOpen}
                      onClickOutside={() => setIsFavSearchCreateOpen(false)}
                    >
                      <Template label='trigger'>
                        <Button
                          size='small'
                          type='primary'
                          onClick={() => {
                            searchingSetCustomErrorMsg('search', '');
                            searchingSeTMeta({
                              search: '',
                            });
                            setIsFavSearchCreateOpen(true);
                          }}
                        >
                          + New Favorite Search
                        </Button>
                      </Template>
                      <Template label='popover'>
                        <Box
                          padding={popoverActionsProps.padding}
                          background={popoverActionsProps.background}
                          width={popoverActionsProps.width}
                        >
                          <Stack space='small'>
                            <FormItem
                              {...searchingForm.search}
                              label='Search Name'
                              state={
                                searchingForm.search?.helpText && searchingForm.search?.helpText.length > 0
                                  ? 'error'
                                  : undefined
                              }
                              onBlur={() => searchingOnBlur('search')}
                            >
                              <TextInput
                                id='search-name-input'
                                value={searchingModel.search}
                                onChange={val => searchingOnChange('search', val)}
                              />
                            </FormItem>
                            <Cluster justify='space-between'>
                              <div></div>
                              <Cluster>
                                <Button ghost={true} onClick={() => setIsFavSearchCreateOpen(false)}>
                                  Cancel
                                </Button>
                                <Button
                                  type='primary'
                                  onClick={createSavedSearch}
                                  state={
                                    isFetching
                                      ? 'thinking'
                                      : !searchingState.isDirty ||
                                        !searchingState.isValid ||
                                        !searchingModel.search?.trim()
                                      ? 'disabled'
                                      : ''
                                  }
                                >
                                  Create
                                </Button>
                              </Cluster>
                            </Cluster>
                          </Stack>
                        </Box>
                      </Template>
                    </Popover>
                  )}
                </Cluster>
              </Stack>
            </Box>
          </Template>
        </Accordion>
      </Stack>
    );
  },
);
